import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Search, Users, User, Calendar, MapPin, FileText, Eye, Filter, ArrowUpDown, ArrowDown, ArrowUp } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { GuestLink, ReservationLink } from '../components/crm/EntityLinks';

export default function PaxListPage() {
  const navigate = useNavigate();
  const [paxList, setPaxList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedPax, setSelectedPax] = useState(null);

  useEffect(() => {
    fetchPaxList();
  }, []);

  const fetchPaxList = async () => {
    try {
      const response = await api.get('/reservations/pax/all');
      setPaxList(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Pays uniques
  const uniqueCountries = [...new Set(paxList.filter(p => p.nationality).map(p => p.nationality))].sort();

  // Filtrage et tri
  const getFilteredPax = () => {
    let filtered = [...paxList];
    
    // Recherche
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(p => 
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchLower) ||
        p.passport_number?.toLowerCase().includes(searchLower) ||
        p.nationality?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filtre pays
    if (filterCountry) {
      filtered = filtered.filter(p => p.nationality === filterCountry);
    }
    
    // Tri
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
          break;
        case 'recent':
          comparison = new Date(b.reservation?.check_in || 0) - new Date(a.reservation?.check_in || 0);
          break;
        case 'country':
          comparison = (a.nationality || 'ZZZ').localeCompare(b.nationality || 'ZZZ');
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  };

  const filteredPax = getFilteredPax();

  return (
    <div className="space-y-6" data-testid="pax-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Registre des voyageurs</h1>
          <p className="text-muted-foreground mt-1">Liste de toutes les personnes ayant séjourné au riad</p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          <Users className="w-5 h-5 mr-2" />
          {filteredPax.length} voyageur{filteredPax.length > 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom ou numéro de passeport..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px]">
                  <ArrowUpDown className="w-3 h-3 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Plus récent</SelectItem>
                  <SelectItem value="name">Nom</SelectItem>
                  <SelectItem value="country">Pays</SelectItem>
                </SelectContent>
              </Select>
              
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
              >
                {sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
              </Button>
              
              {uniqueCountries.length > 0 && (
                <Select value={filterCountry || 'all'} onValueChange={(v) => setFilterCountry(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Tous pays" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous pays</SelectItem>
                    {uniqueCountries.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : filteredPax.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun voyageur trouvé</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPax.map((pax) => (
            <Card 
              key={pax.id} 
              className="card-metric hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedPax(pax)}
              data-testid={`pax-card-${pax.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{pax.first_name} {pax.last_name}</h3>
                      {pax.nationality && (
                        <p className="text-sm text-muted-foreground">{pax.nationality}</p>
                      )}
                    </div>
                  </div>
                  {pax.is_main_guest && (
                    <Badge variant="outline" className="bg-primary/10 text-primary">Principal</Badge>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  {pax.passport_number && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <FileText className="w-4 h-4" />
                      <span>{pax.passport_number}</span>
                    </div>
                  )}
                  
                  {pax.reservation && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <ReservationLink 
                        reservation={{ 
                          id: pax.reservation.id, 
                          reservation_number: pax.reservation.reservation_number 
                        }} 
                        showIcon={false}
                        className="text-sm"
                      />
                      <span className="text-xs">
                        ({format(new Date(pax.reservation.check_in), 'dd MMM', { locale: fr })})
                      </span>
                    </div>
                  )}
                  
                  {pax.reservation?.guest && (
                    <div className="pt-2 border-t">
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        Client: <GuestLink 
                          guest={pax.reservation.guest} 
                          showIcon={false}
                          className="text-xs font-medium"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog détail */}
      <Dialog open={!!selectedPax} onOpenChange={() => setSelectedPax(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Détail du voyageur</DialogTitle>
          </DialogHeader>
          {selectedPax && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{selectedPax.first_name} {selectedPax.last_name}</h3>
                  {selectedPax.nationality && (
                    <p className="text-muted-foreground">{selectedPax.nationality}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-xs text-muted-foreground">Passeport</Label>
                  <p className="font-medium">{selectedPax.passport_number || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Date de naissance</Label>
                  <p className="font-medium">
                    {selectedPax.birth_date 
                      ? format(new Date(selectedPax.birth_date), 'dd/MM/yyyy') 
                      : '-'}
                  </p>
                </div>
              </div>
              
              {selectedPax.reservation && (
                <div className="p-4 border rounded-lg space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Réservation</Label>
                    <div className="mt-1">
                      <ReservationLink 
                        reservation={{ 
                          id: selectedPax.reservation.id, 
                          reservation_number: selectedPax.reservation.reservation_number 
                        }}
                        variant="badge"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Séjour</Label>
                    <p className="font-medium">
                      {format(new Date(selectedPax.reservation.check_in), 'dd MMM', { locale: fr })} → {format(new Date(selectedPax.reservation.check_out), 'dd MMM yyyy', { locale: fr })}
                    </p>
                  </div>
                  {selectedPax.reservation.guest && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Client</Label>
                      <div className="mt-1">
                        <GuestLink 
                          guest={selectedPax.reservation.guest}
                          variant="badge"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
